<template>
  <Dialog :open="open" @update:open="handleClose" title="Regenerate API Key" size="lg">
    <div class="w-full" data-testid="confirm-regenerate-modal">
      <!-- Success State - Show New Key -->
      <div v-if="regeneratedKey" class="space-y-4">
        <div class="bg-green-50 border border-green-200 rounded-md p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-green-800">API Key Regenerated Successfully!</h3>
              <div class="mt-2 text-sm text-green-700">
                <p>
                  Your API key has been regenerated. Make sure to copy it now - you won't be able to
                  see it again!
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="space-y-3">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">API Key Name</label>
            <p class="text-sm text-gray-900">{{ apiKey?.name }}</p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">New API Key</label>
            <div class="flex items-center space-x-2">
              <code
                class="flex-1 bg-gray-100 border border-gray-300 rounded-md px-3 py-2 text-sm font-mono text-gray-900 select-all"
              >
                {{ regeneratedKey.key }}
              </code>
              <Button variant="outline" size="sm" @click="copyNewKey" class="flex-shrink-0">
                <svg class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                {{ copied ? 'Copied!' : 'Copy' }}
              </Button>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Masked Key</label>
            <code
              class="bg-gray-100 border border-gray-300 rounded-md px-3 py-2 text-sm font-mono text-gray-900"
            >
              {{ regeneratedKey.masked_key }}
            </code>
            <p class="mt-1 text-xs text-gray-500">
              This is how your key will appear in the dashboard for identification.
            </p>
          </div>

          <div class="bg-red-50 border border-red-200 rounded-md p-3">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg
                  class="h-5 w-5 text-red-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-red-800">Important Notice</h3>
                <div class="mt-2 text-sm text-red-700">
                  <p>
                    The previous API key has been invalidated. Make sure to update any systems using
                    the old key.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="flex justify-end pt-4">
          <Button @click="handleClose">Done</Button>
        </div>
      </div>

      <!-- Confirmation State -->
      <div v-else class="space-y-4">
        <div>
          <h3 class="text-lg font-medium text-gray-900">
            Regenerate API Key "{{ apiKey?.name }}"?
          </h3>
          <div class="mt-2">
            <p class="text-sm text-gray-500">
              This action will invalidate the current API key and generate a new one. Any systems
              using the current key will need to be updated.
            </p>
          </div>
        </div>

        <!-- Current Key Info -->
        <Card padding="sm">
          <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Current API Key</h4>
          <div class="space-y-2">
            <div>
              <span class="text-xs text-gray-500 dark:text-gray-400">Masked Key:</span>
              <code class="ml-2 text-sm font-mono text-gray-900 dark:text-gray-100">{{
                getMaskedKey(apiKey)
              }}</code>
            </div>
            <div>
              <span class="text-xs text-gray-500 dark:text-gray-400">Created:</span>
              <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">{{
                formatDate(apiKey?.created_at)
              }}</span>
            </div>
          </div>
        </Card>

        <!-- Warning -->
        <div class="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg
                class="h-5 w-5 text-yellow-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-yellow-800">Warning</h3>
              <div class="mt-2 text-sm text-yellow-700">
                <ul class="list-disc list-inside space-y-1">
                  <li>The current API key will be immediately invalidated</li>
                  <li>Any applications using this key will stop working</li>
                  <li>You'll need to update all systems with the new key</li>
                  <li>This action cannot be undone</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex justify-end space-x-3 pt-4">
          <Button type="button" variant="outline" @click="handleClose">Cancel</Button>
          <Button
            type="button"
            variant="danger"
            @click="handleRegenerate"
            :loading="loading"
            data-testid="confirm-regenerate-button"
          >
            Regenerate API Key
          </Button>
        </div>
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
  import { ref } from 'vue';
  import { Dialog, Button, Card, useNotification } from '@asabdev/asab-ui';
  import { formatApiKeyDate } from '@/features/api-keys/utils';
  import { useApiKeys } from '@/features/api-keys/composables';
  import type { ApiKey, ApiKeyRotateResponse } from '@/features/api-keys/types';
import { toUserMessage } from '@/lib/api/errors';

  interface Props {
    open: boolean;
    apiKey: ApiKey | null;
  }

  interface Emits {
    (e: 'update:open', value: boolean): void;
    (e: 'regenerated'): void;
  }

  const props = defineProps<Props>();
  const emit = defineEmits<Emits>();

  const { showSuccess, showError } = useNotification();
  const { rotateApiKey } = useApiKeys();

  // State
  const loading = ref(false);
  const regeneratedKey = ref<ApiKeyRotateResponse | null>(null);
  const copied = ref(false);

  // Methods
  const getMaskedKey = (apiKey: ApiKey | null) => {
    if (!apiKey) return '';
    return apiKey.key_prefix + '****';
  };

  const formatDate = (dateString?: string) => {
    return dateString ? formatApiKeyDate(dateString) : 'Never';
  };

  const copyNewKey = async () => {
    if (!regeneratedKey.value) return;

    try {
      await navigator.clipboard.writeText(regeneratedKey.value.key);
      copied.value = true;
      showSuccess('API key copied to clipboard!');

      // Reset copied state after 2 seconds
      setTimeout(() => {
        copied.value = false;
      }, 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      showError('Failed to copy to clipboard');
    }
  };

  const handleRegenerate = async () => {
    if (!props.apiKey) return;

    loading.value = true;
    try {
      const response = await rotateApiKey(props.apiKey.id);

      if (response) {
        regeneratedKey.value = response;
        showSuccess(`API key "${props.apiKey.name}" rotated successfully!`);
        emit('regenerated');
      } else {
        showError('Failed to rotate API key');
      }
    } catch (error) {
      const errorMessage = toUserMessage(error, 'Unknown error occurred');
      showError(`Failed to rotate API key: ${errorMessage}`);
    } finally {
      loading.value = false;
    }
  };

  const handleClose = () => {
    // Reset state when closing
    regeneratedKey.value = null;
    copied.value = false;
    emit('update:open', false);
  };
</script>
